import {JSXElementConstructor, lazy, LazyExoticComponent, ReactElement, Suspense} from 'react';
import centerLesion from '@legit-health/svg-images/images/instructions/center-the-lesion.svg';
import noCover from '@legit-health/svg-images/images/instructions/do-not-cover-the-lesion.svg';
import noShadow from '@legit-health/svg-images/images/instructions/no-shadow.svg';
import {BodySiteCode, Condition, Perspective} from '@legit.health/ui';
import {Skeleton} from '@mui/material';
import {TFunction} from 'i18next';
import isAlopecia from '@/shared/utils/isAlopecia';
import BottomBar from '../../../../steps/BottomBar';
import BaseInstructionsStep from '../../../../steps/InstructionsStep';
import {ConditionInfo} from '../../../../types';
import CustomInstructions from './components/CustomInstructions';

const AlopeciaInstructions = lazy(() => import('./components/AlopeciaInstructions'));

const instructionComponents: Record<
  string,
  Record<
    BodySiteCode,
    LazyExoticComponent<
      (props: {
        t: TFunction;
        condition: Condition | null;
        perspective?: Perspective | null;
        onSelectPerspective: (perspective: Perspective) => void;
      }) => ReactElement<any, string | JSXElementConstructor<any>>
    >
  >
> = {};

export default function Instructions({
  activeStep,
  conditionInfo,
  condition,
  onBack,
  onNext,
  onSelectPerspective,
  perspective,
  t,
  totalSteps
}: {
  activeStep: number;
  conditionInfo?: ConditionInfo | null;
  condition?: Condition;
  onBack: () => void;
  onNext: () => void;
  onSelectPerspective?: (perspective: Perspective) => void;
  perspective?: Perspective | null;
  t: TFunction;
  totalSteps: number;
}) {
  const conditionBodySite = condition?.bodySite;
  const conditionPathology = condition?.pathology;

  if (conditionPathology && isAlopecia(conditionPathology)) {
    return (
      <Suspense fallback={<Skeleton height={200} width="100%" />}>
        <AlopeciaInstructions t={t} />
        <BottomBar
          isBackEnabled
          isNextEnabled
          onNext={onNext}
          t={t}
          onBack={onBack}
          activeStep={activeStep}
          totalSteps={totalSteps}
        />
      </Suspense>
    );
  }

  const requiresPerspectiveForBodySite = conditionBodySite
    ? conditionInfo?.perspectivesByBodySite[conditionBodySite.code]
    : null;
  if (
    requiresPerspectiveForBodySite &&
    conditionBodySite &&
    conditionPathology &&
    onSelectPerspective
  ) {
    const Component = instructionComponents[conditionPathology.code]?.[conditionBodySite.code];
    if (Component) {
      return (
        <Suspense fallback={<Skeleton height={200} width="100%" />}>
          <Component
            t={t}
            condition={condition ?? null}
            perspective={perspective}
            onSelectPerspective={onSelectPerspective}
          />
          <BottomBar
            isBackEnabled
            isNextEnabled
            onNext={onNext}
            t={t}
            onBack={onBack}
            activeStep={activeStep}
            totalSteps={totalSteps}
          />
        </Suspense>
      );
    }
  }

  if (
    conditionPathology &&
    (conditionPathology.instructions || conditionPathology.instructionsYoutubeId)
  ) {
    return (
      <>
        <CustomInstructions
          instructions={conditionPathology.instructions}
          instructionsYoutubeId={conditionPathology.instructionsYoutubeId}
          t={t}
        />
        <BottomBar
          isBackEnabled
          isNextEnabled
          onNext={onNext}
          t={t}
          onBack={onBack}
          activeStep={activeStep}
          totalSteps={totalSteps}
        />
      </>
    );
  }

  const instructions = [
    {
      title:
        condition && condition.firstImage
          ? t('shared/common:diagnosticReport.form.instructions.rememberFirstPhoto.title')
          : t('shared/common:diagnosticReport.form.instructions.rememberFirstPhoto.title'),
      description:
        condition && condition.firstImage
          ? t('shared/common:diagnosticReport.form.instructions.rememberFirstPhoto.description')
          : t('shared/common:diagnosticReport.form.instructions.rememberFirstPhoto.description'),
      image: condition?.firstImage ?? centerLesion
    },
    {
      title: t('shared/common:diagnosticReport.form.instructions.avoidShadows.title'),
      description: t('shared/common:diagnosticReport.form.instructions.avoidShadows.description'),
      image: noShadow
    },
    {
      title: t('shared/common:diagnosticReport.form.instructions.dontCover.title'),
      description: t('shared/common:diagnosticReport.form.instructions.dontCover.description'),
      image: noCover
    }
  ];

  return (
    <BaseInstructionsStep
      activeStep={activeStep}
      totalSteps={totalSteps}
      t={t}
      instructions={instructions}
      onNext={onNext}
      onBack={onBack}
    />
  );
}
